import { useTranslation } from 'react-i18next';
import { Apple, Download, MonitorDown } from 'lucide-react';
import { Heading } from '@/components/layout/Heading';
import { PLATFORMS, type Platform } from '@/lib/db';
import { formatDateMedium } from '@/lib/dates';
import type { Release } from '@/lib/releases';
import { MarkdownNotes } from './MarkdownNotes';
import { channelI18nKey } from './helpers';

interface ReleaseEntryProps {
  release: Release;
}

interface DownloadLink {
  platform: Platform;
  href: string;
  labelKey: 'release.windows' | 'release.macos';
}

function platformIcon(p: Platform) {
  return p === PLATFORMS.macos ? (
    <Apple className="h-4 w-4" aria-hidden="true" />
  ) : (
    <MonitorDown className="h-4 w-4" aria-hidden="true" />
  );
}

export function ReleaseEntry({ release }: ReleaseEntryProps) {
  const { t, i18n } = useTranslation('changelog');

  const downloads: DownloadLink[] = [];
  if (release.windowsUrl) {
    downloads.push({
      platform: PLATFORMS.windows,
      href: release.windowsUrl,
      labelKey: 'release.windows',
    });
  }
  if (release.macosUrl) {
    downloads.push({
      platform: PLATFORMS.macos,
      href: release.macosUrl,
      labelKey: 'release.macos',
    });
  }

  return (
    <article className="rounded-2xl border border-border bg-card/50 p-6 shadow-soft sm:p-8">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Heading level={3} as="h2" className="font-mono">
            {t('release.version_label', { version: release.version })}
          </Heading>
          <p className="mt-2 font-mono text-sm text-muted-foreground">
            {t('release.released_label', {
              date: formatDateMedium(release.releasedAt, i18n.language),
            })}
          </p>
        </div>
        <span className="inline-flex items-center rounded-full border border-border bg-background px-3 py-1 text-xs font-medium text-muted-foreground">
          {t(channelI18nKey(release.channel))}
        </span>
      </header>

      <div className="mt-6">
        {release.notesMd && release.notesMd.trim().length > 0 ? (
          <MarkdownNotes source={release.notesMd} />
        ) : (
          <p className="text-sm italic text-muted-foreground">{t('release.notes_empty')}</p>
        )}
      </div>

      {downloads.length > 0 ? (
        <footer className="mt-6 border-t border-border pt-4">
          <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            {t('release.downloads_label')}
          </p>
          <div className="flex flex-wrap gap-2">
            {downloads.map((d) => (
              <a
                key={d.platform}
                href={d.href}
                className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-xs font-medium text-foreground transition-colors duration-fast ease-out-quart hover:border-brand/50 hover:text-brand"
              >
                {platformIcon(d.platform)}
                <span>{t(d.labelKey)}</span>
                <Download className="h-3.5 w-3.5" aria-hidden="true" />
              </a>
            ))}
          </div>
        </footer>
      ) : null}
    </article>
  );
}
